import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Globe, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/Button';
// ⚠️ Replace with your Formspree form ID from https://formspree.io
const FORMSPREE_ID = 'xdalovgl';
export function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };
  return <section id="contact" className="py-24 bg-dark-lighter" data-id="element-29">
      <div className="max-w-[1320px] mx-auto px-4" data-id="element-30">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-31">
          {/* Contact Info */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-32">
            <h2 className="text-4xl md:text-5xl font-bold text-white font-syne mb-6" data-id="element-33">
              Let's Work <br data-id="element-34" />
              <span className="text-gold" data-id="element-35">Together</span>
            </h2>
            <p className="text-white/65 text-lg font-inter mb-12 max-w-md" data-id="element-36">
              Have a project in mind? I'm always open to discussing new
              projects, creative ideas or opportunities to be part of your
              visions.
            </p>

            <div className="space-y-8" data-id="element-37">
              <div className="flex items-start gap-6 group" data-id="element-38">
                <div className="w-14 h-14 bg-dark rounded-full flex items-center justify-center text-gold border border-white/10 group-hover:border-gold transition-colors shrink-0" data-id="element-39">
                  <Phone className="w-6 h-6" data-id="element-40" />
                </div>
                <div data-id="element-41">
                  <h4 className="text-white font-syne font-bold text-xl mb-1" data-id="element-42">
                    Call Me
                  </h4>
                  <a href="tel:+27712158291" className="text-white/60 font-inter hover:text-gold transition-colors" data-id="element-43">
                    071 215 8291
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-6 group" data-id="element-44">
                <div className="w-14 h-14 bg-dark rounded-full flex items-center justify-center text-gold border border-white/10 group-hover:border-gold transition-colors shrink-0" data-id="element-45">
                  <Globe className="w-6 h-6" data-id="element-46" />
                </div>
                <div data-id="element-47">
                  <h4 className="text-white font-syne font-bold text-xl mb-1" data-id="element-48">
                    Website
                  </h4>
                  <a href="https://byteweb.co.za" target="_blank" rel="noopener noreferrer" className="text-white/60 font-inter hover:text-gold transition-colors" data-id="element-49">
                    byteweb.co.za
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-6 group" data-id="element-50">
                <div className="w-14 h-14 bg-dark rounded-full flex items-center justify-center text-gold border border-white/10 group-hover:border-gold transition-colors shrink-0" data-id="element-51">
                  <MapPin className="w-6 h-6" data-id="element-52" />
                </div>
                <div data-id="element-53">
                  <h4 className="text-white font-syne font-bold text-xl mb-1" data-id="element-54">
                    Location
                  </h4>
                  <p className="text-white/60 font-inter" data-id="element-55">
                    Cape Town, South Africa
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="bg-dark p-8 md:p-10 rounded-2xl border border-white/5" data-id="element-56">
            <AnimatePresence mode="wait" data-id="element-57">
              {status === 'success' ? <motion.div key="success" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} exit={{
              opacity: 0,
              scale: 0.9
            }} className="flex flex-col items-center justify-center text-center py-16" data-id="element-58">
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                delay: 0.1
              }} data-id="element-59">
                    <CheckCircle className="w-16 h-16 text-gold mb-6" data-id="element-60" />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-white font-syne mb-3" data-id="element-61">
                    Message Sent!
                  </h3>
                  <p className="text-white/60 font-inter mb-8 max-w-sm" data-id="element-62">
                    Thanks for reaching out. I'll get back to you as soon as
                    possible.
                  </p>
                  <button onClick={() => setStatus('idle')} className="text-gold font-inter font-medium hover:text-white transition-colors" data-id="element-63">
                    Send another message →
                  </button>
                </motion.div> : <motion.form key="form" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="space-y-6" onSubmit={handleSubmit} data-id="element-64">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-65">
                    <div className="space-y-2" data-id="element-66">
                      <label className="text-white/80 font-inter text-sm font-medium" data-id="element-67">
                        Name
                      </label>
                      <input type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="Your Name" className="w-full bg-dark-lighter border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors" data-id="element-68" />
                    </div>
                    <div className="space-y-2" data-id="element-69">
                      <label className="text-white/80 font-inter text-sm font-medium" data-id="element-70">
                        Email
                      </label>
                      <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="Your Email" className="w-full bg-dark-lighter border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors" data-id="element-71" />
                    </div>
                  </div>

                  <div className="space-y-2" data-id="element-72">
                    <label className="text-white/80 font-inter text-sm font-medium" data-id="element-73">
                      Subject
                    </label>
                    <input type="text" name="subject" required value={formData.subject} onChange={handleChange} placeholder="Project Subject" className="w-full bg-dark-lighter border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors" data-id="element-74" />
                  </div>

                  <div className="space-y-2" data-id="element-75">
                    <label className="text-white/80 font-inter text-sm font-medium" data-id="element-76">
                      Message
                    </label>
                    <textarea name="message" rows={4} required value={formData.message} onChange={handleChange} placeholder="Tell me about your project..." className="w-full bg-dark-lighter border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/30 focus:outline-none focus:border-gold transition-colors resize-none" data-id="element-77"></textarea>
                  </div>

                  {status === 'error' && <motion.div initial={{
                opacity: 0,
                y: -8
              }} animate={{
                opacity: 1,
                y: 0
              }} className="flex items-center gap-3 text-red-400 font-inter text-sm bg-red-400/10 px-4 py-3 rounded-lg" data-id="element-78">
                      <AlertCircle className="w-5 h-5 shrink-0" data-id="element-79" />
                      Something went wrong. Please try again or email me
                      directly.
                    </motion.div>}

                  <Button className="w-full md:w-auto" disabled={status === 'submitting'} data-id="element-80">
                    {status === 'submitting' ? <>
                        Sending...{' '}
                        <Loader2 className="ml-2 w-4 h-4 animate-spin" data-id="element-81" />
                      </> : <>
                        Send Message <Send className="ml-2 w-4 h-4" data-id="element-82" />
                      </>}
                  </Button>
                </motion.form>}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>;
}